<template>
  <div class="w-full min-w-0">
    <div class="relative">
      <!-- 空の判定を :placeholder-shown に任せるため、例示のプレースホルダは受け付けない -->
      <input v-bind="$attrs" :id="inputId" placeholder=" " :aria-required="required" :class="inputClass" :value="modelValue" @input="onInput" />
      <label :class="labelClass" :for="inputId">{{ label }}<span v-if="required" aria-hidden="true" class="ml-0.5 text-light-fg-danger">*</span></label>
    </div>
    <AtomsErrorText v-if="isError" class="mt-1 block" lang="en">{{ error }}</AtomsErrorText>
  </div>
</template>

<script setup lang="ts">
import { tv } from 'tailwind-variants';
import { computed, useId } from 'vue';

import AtomsErrorText from '@/components/atoms/ErrorText/ErrorText.vue';

defineOptions({ inheritAttrs: false });

interface Props {
  label: string;
  modelValue?: string;
  onUpdateModelValue?: (value: string) => void;
  required?: boolean;
  error?: string;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  onUpdateModelValue: undefined,
  required: true,
  error: '',
});

const inputId = useId();

const isError = computed(() => props.error !== '');

const onInput = (event: Event) => props.onUpdateModelValue?.((event.target as HTMLInputElement).value);

const inputStyle = tv({
  base: 'peer box-border h-12 w-full appearance-none rounded-none border border-solid border-light-bd-muted bg-light-bg-default px-4 text-sm text-light-fg-default outline-none transition-colors duration-100 ease-in-out focus:border-light-bd-accent',
  variants: {
    isError: {
      true: 'border-light-bd-danger focus:border-light-bd-danger',
    },
  },
});

// 上がったラベルは背景色で枠線を切る。オートフィルは入力イベントより先に値が見えることがあるため CSS でも拾う
const labelStyle = tv({
  base: 'pointer-events-none absolute left-3 top-0 max-w-[calc(100%-1.5rem)] -translate-y-1/2 truncate bg-light-bg-default px-1 text-[11px] leading-4 text-light-fg-accent transition-all duration-100 ease-in-out peer-placeholder-shown:top-1/2 peer-placeholder-shown:bg-transparent peer-placeholder-shown:text-sm peer-placeholder-shown:text-light-fg-muted peer-autofill:top-0 peer-autofill:bg-light-bg-default peer-autofill:text-[11px] peer-focus:top-0 peer-focus:bg-light-bg-default peer-focus:text-[11px] peer-focus:text-light-fg-accent',
  variants: {
    isError: {
      true: 'text-light-fg-danger peer-focus:text-light-fg-danger',
    },
  },
});

const inputClass = computed(() => inputStyle({ isError: isError.value }));
const labelClass = computed(() => labelStyle({ isError: isError.value }));
</script>
